// ** Modules
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import type { SearchOption } from '@/components/common/searchSection/types';

// ** Services
import {
	fetchMegaMenuResults,
	fetchMegaMenuSuggestions,
	fetchMegaMenuSearchFilterOptions
} from '@/features/ap07/services/ap07.mega-menu-search.service';

// ** Local
import { iconButtonBaseSx, srOnlySx } from './megaMenuOverlay.constants';
import SearchResultCard from './SearchResultCard';
import SearchFilterRow from './SearchFilterRow';
import SearchResultsHeadline from './SearchResultsHeadline';

// ** Types
import type { SearchState, SearchSuggestion, SearchResult, SearchViewProps } from './megaMenuOverlay.types';

/** Figma reference width; bar is `width: 100%` capped with `maxWidth`. */
const SEARCH_BAR_MAX_WIDTH = 846;

const SearchView = ({
	shouldReduceMotion,
	emptyStateHeading = 'Nichts gefunden?',
	emptyStateContactLabel = 'Kontaktieren Sie uns:',
	emptyStateEmailLine = 'Email: hello@tischler.nrw',
	emptyStatePhoneLine = 'Telefon: 012345678',
	filterTypOptions: filterTypOptionsProp,
	filterThemaOptions: filterThemaOptionsProp
}: SearchViewProps) => {
	const theme = useTheme();
	const inputRef = React.useRef<HTMLInputElement>(null);

	const [query, setQuery] = React.useState('');
	const [searchState, setSearchState] = React.useState<SearchState>('idle');
	const [suggestions, setSuggestions] = React.useState<SearchSuggestion[]>([]);
	const [results, setResults] = React.useState<SearchResult[]>([]);
	const [filterTyp, setFilterTyp] = React.useState('');
	const [filterThema, setFilterThema] = React.useState('');
	const [activeSuggestionIndex, setActiveSuggestionIndex] = React.useState(-1);

	const [loadedTypOptions, setLoadedTypOptions] = React.useState<SearchOption[]>([]);
	const [loadedThemaOptions, setLoadedThemaOptions] = React.useState<SearchOption[]>([]);

	const typOptions = filterTypOptionsProp ?? loadedTypOptions;
	const themaOptions = filterThemaOptionsProp ?? loadedThemaOptions;

	const suggestionDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchAbortRef = React.useRef<AbortController | null>(null);

	const white = theme.palette.common.white;
	const overlayBrown = theme.palette.loginOverlayCta.main;

	React.useEffect(() => {
		if (filterTypOptionsProp && filterThemaOptionsProp) return;
		let cancelled = false;
		fetchMegaMenuSearchFilterOptions().then((opts) => {
			if (cancelled) return;
			if (!filterTypOptionsProp) setLoadedTypOptions(opts.typOptions);
			if (!filterThemaOptionsProp) setLoadedThemaOptions(opts.themaOptions);
		});
		return () => {
			cancelled = true;
		};
	}, [filterTypOptionsProp, filterThemaOptionsProp]);

	React.useEffect(() => {
		const timer = setTimeout(() => {
			inputRef.current?.focus({ preventScroll: true });
		}, 80);
		return () => clearTimeout(timer);
	}, []);

	React.useEffect(() => {
		return () => {
			searchAbortRef.current?.abort();
		};
	}, []);

	const handleQueryChange = (value: string) => {
		setQuery(value);
		setActiveSuggestionIndex(-1);

		if (suggestionDebounceRef.current) {
			clearTimeout(suggestionDebounceRef.current);
			suggestionDebounceRef.current = null;
		}

		if (!value.trim() || value.trim().length < 2) {
			setSearchState('idle');
			setSuggestions([]);
			return;
		}

		suggestionDebounceRef.current = setTimeout(async () => {
			suggestionDebounceRef.current = null;
			const fetched = await fetchMegaMenuSuggestions(value);
			setSuggestions(fetched);
			setSearchState('suggestions');
		}, 300);
	};

	const executeSearch = React.useCallback(
		async (q: string) => {
			const trimmed = q.trim();
			if (!trimmed) return;

			if (suggestionDebounceRef.current) {
				clearTimeout(suggestionDebounceRef.current);
				suggestionDebounceRef.current = null;
			}

			searchAbortRef.current?.abort();
			const controller = new AbortController();
			searchAbortRef.current = controller;

			setSearchState('loading');
			setSuggestions([]);

			try {
				const fetched = await fetchMegaMenuResults(
					trimmed,
					{
						typ: filterTyp || undefined,
						thema: filterThema || undefined
					},
					controller.signal
				);

				if (controller.signal.aborted) return;

				if (fetched.length > 0) {
					setResults(fetched);
					setSearchState('results');
				} else {
					setResults([]);
					setSearchState('empty');
				}
			} catch {
				if (!controller.signal.aborted) {
					setResults([]);
					setSearchState('empty');
				}
			}
		},
		[filterTyp, filterThema]
	);

	const handleSubmit = () => executeSearch(query);

	const handleSuggestionClick = (label: string) => {
		setQuery(label);
		executeSearch(label);
	};

	const handleClearInput = () => {
		setQuery('');
		setSearchState('idle');
		setSuggestions([]);
		setResults([]);
		setFilterTyp('');
		setFilterThema('');
		setActiveSuggestionIndex(-1);
		searchAbortRef.current?.abort();
		inputRef.current?.focus();
	};

	const suggestionItemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

	React.useEffect(() => {
		if (searchState !== 'suggestions' || suggestions.length === 0) {
			setActiveSuggestionIndex(-1);
			return;
		}
		setActiveSuggestionIndex((i) => {
			if (i >= suggestions.length) return suggestions.length - 1;
			return i;
		});
	}, [searchState, suggestions]);

	React.useEffect(() => {
		if (activeSuggestionIndex < 0) return;
		suggestionItemRefs.current[activeSuggestionIndex]?.scrollIntoView({
			block: 'nearest',
			behavior: 'smooth'
		});
	}, [activeSuggestionIndex]);

	// ── Shared styles ──────────────────────────────────────────────────────────

	const liveRegionMessage = React.useMemo(() => {
		switch (searchState) {
			case 'loading':
				return `Suche nach ${query || 'Begriff'} wird geladen.`;
			case 'suggestions':
				return `${suggestions.length} Suchvorschläge verfügbar.`;
			case 'results':
				return `${results.length} ${results.length === 1 ? 'Ergebnis' : 'Ergebnisse'} gefunden.`;
			case 'empty':
				return `Keine Ergebnisse für ${query}.`;
			default:
				return '';
		}
	}, [query, results.length, searchState, suggestions.length]);

	const emptyStateLeadSx = {
		m: 0,
		color: 'common.white',
		...theme.typography['body-lg'],
		[theme.breakpoints.up('md')]: {
			...theme.typography['body-xl']
		}
	};

	const emptyStateDetailSx = {
		m: 0,
		color: 'common.white',
		...theme.typography['body-lg'],
		fontWeight: 300,
		[theme.breakpoints.up('md')]: {
			...theme.typography['body-xl'],
			fontWeight: 300
		}
	};

	const filterRowProps = {
		filterTyp,
		filterThema,
		typOptions,
		themaOptions,
		onFilterTypChange: (val: string | null) => setFilterTyp(val ?? ''),
		onFilterThemaChange: (val: string | null) => setFilterThema(val ?? '')
	} as const;

	// ── Render ─────────────────────────────────────────────────────────────────

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				minHeight: '100%',
				boxSizing: 'border-box',
				backgroundColor: overlayBrown,
				px: {
					xs: theme.spacing(theme.layout.pagePx.xs),
					md: theme.spacing(theme.layout.pagePx.md),
					lg: theme.spacing(theme.layout.pagePx.lg)
				},
				alignItems: { xs: 'stretch', md: 'center' },
				pt: {
					xs: 0,
					md: `calc(176px - ${theme.spacing(4)} - 64px - ${theme.spacing(8)})`
				}
			}}
		>
			<Box component="span" sx={srOnlySx} aria-live="polite" aria-atomic="true">
				{liveRegionMessage}
			</Box>

			{/* ── Search input ── */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					width: '100%',
					maxWidth: { xs: 'none', md: SEARCH_BAR_MAX_WIDTH },
					minHeight: { xs: 'auto', md: 64 },
					boxSizing: 'border-box',
					backgroundColor: 'common.white',
					borderRadius: '8px',
					pl: { xs: 3, md: 4 },
					pr: { xs: 3, md: 4 },
					py: { xs: 2, md: 0 }
				}}
			>
				<InputBase
					inputRef={inputRef}
					type="search"
					value={query}
					onChange={(e) => handleQueryChange(e.target.value)}
					onKeyDown={(e) => {
						const hasSuggestions = searchState === 'suggestions' && suggestions.length > 0;

						if (hasSuggestions && e.key === 'ArrowDown') {
							e.preventDefault();
							setActiveSuggestionIndex((i) => Math.min(i + 1, suggestions.length - 1));
							return;
						}
						if (hasSuggestions && e.key === 'ArrowUp') {
							e.preventDefault();
							setActiveSuggestionIndex((i) => Math.max(i - 1, -1));
							return;
						}
						if (hasSuggestions && e.key === 'Escape') {
							e.preventDefault();
							setActiveSuggestionIndex(-1);
							return;
						}
						if (e.key === 'Enter') {
							if (hasSuggestions && activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
								e.preventDefault();
								handleSuggestionClick(suggestions[activeSuggestionIndex].label);
								return;
							}
							handleSubmit();
						}
					}}
					placeholder="Suchen…"
					fullWidth
					inputProps={{
						'aria-label': 'Suchfeld',
						'aria-autocomplete': 'list',
						'aria-expanded': searchState === 'suggestions' && suggestions.length > 0,
						'aria-controls': 'mega-menu-suggestions-list',
						...(activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]
							? {
									'aria-activedescendant': `mega-menu-suggestion-${suggestions[activeSuggestionIndex].id}`
								}
							: {})
					}}
					sx={{
						flex: 1,
						minWidth: 0,
						pl: 0,
						pr: 0,
						typography: 'body-md',
						color: 'text.primary',
						'& .MuiInputBase-input': {
							p: 0,
							'&::-webkit-search-cancel-button': {
								WebkitAppearance: 'none',
								appearance: 'none'
							},
							[theme.breakpoints.up('md')]: {
								fontSize: '18px',
								lineHeight: '26px',
								color: 'common.black'
							},
							'&::placeholder': {
								color: theme.palette.grey[500],
								opacity: 1
							}
						}
					}}
				/>

				<Box
					component="button"
					type="button"
					aria-label="Eingabe löschen"
					aria-hidden={!query}
					tabIndex={query ? 0 : -1}
					onClick={handleClearInput}
					sx={{
						...iconButtonBaseSx,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						flexShrink: 0,
						alignSelf: 'center',
						width: 40,
						minHeight: 40,
						padding: 0,
						margin: 0,
						color: theme.palette.grey[700],
						opacity: query ? 1 : 0,
						pointerEvents: query ? 'auto' : 'none',
						transition: 'opacity 0.15s',
						'&:focusVisible': {
							outline: `2px solid ${alpha(theme.palette.grey[600], 0.5)}`,
							outlineOffset: 2
						}
					}}
				>
					<CloseRoundedIcon sx={{ fontSize: 22, display: 'block' }} />
				</Box>
			</Box>

			{/* ── Below-input states ── */}
			<Box
				sx={{
					mt: 4,
					width: '100%',
					maxWidth: { xs: 'none', md: SEARCH_BAR_MAX_WIDTH },
					mx: { md: 'auto' }
				}}
				aria-busy={searchState === 'loading'}
			>
				<AnimatePresence mode="wait" initial={false}>
					{/* Suggestions */}
					{searchState === 'suggestions' && suggestions.length > 0 && (
						<motion.div
							key="suggestions"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.15 }}
						>
							<Box
								sx={{
									borderRadius: '8px',
									backgroundColor: alpha(white, 0.18),
									px: { xs: 3, md: 4 },
									pt: { xs: 3, md: 3 },
									pb: { xs: 6 }
								}}
							>
								<Typography
									component="p"
									variant="body-md"
									sx={{
										m: 0,
										color: alpha(white, 0.64),
										fontSize: { xs: '16px', md: '18px' },
										lineHeight: { xs: '24px', md: '26px' },
										fontWeight: 400,
										mb: 6
									}}
								>
									Suchvorschläge
								</Typography>

								<Box
									component="ul"
									id="mega-menu-suggestions-list"
									role="listbox"
									aria-label="Suchvorschläge"
									sx={{ listStyle: 'none', m: 0, p: 0 }}
								>
									{suggestions.map((suggestion, index) => {
										const isActive = index === activeSuggestionIndex;
										return (
											<Box key={suggestion.id} component="li" role="presentation" sx={{ listStyle: 'none' }}>
												<Box
													component="button"
													type="button"
													id={`mega-menu-suggestion-${suggestion.id}`}
													role="option"
													tabIndex={-1}
													aria-selected={isActive}
													ref={(el: HTMLButtonElement | null) => {
														suggestionItemRefs.current[index] = el;
													}}
													onMouseEnter={() => setActiveSuggestionIndex(index)}
													onClick={() => handleSuggestionClick(suggestion.label)}
													aria-label={`Suchvorschlag: ${suggestion.label}`}
													sx={{
														display: 'block',
														width: '100%',
														textAlign: 'left',
														border: 'none',
														background: isActive ? alpha(white, 0.14) : 'transparent',
														cursor: 'pointer',
														px: 0,
														py: 1.5,
														pl: isActive ? 1 : 0,
														color: 'common.white',
														typography: 'body-md',
														borderRadius: '4px',
														transition: 'background-color 0.15s ease, padding-left 0.15s ease',
														'&:hover': {
															backgroundColor: alpha(white, 0.1),
															pl: 1
														},
														'&:focusVisible': {
															outline: `2px solid ${alpha(white, 0.5)}`,
															outlineOffset: 2
														}
													}}
												>
													{suggestion.label}
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</motion.div>
					)}

					{/* Loading */}
					{searchState === 'loading' && (
						<motion.div
							key="loading"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							style={{
								display: 'flex',
								justifyContent: 'center',
								paddingTop: 48
							}}
						>
							<CircularProgress size={36} thickness={3} sx={{ color: alpha(white, 0.7) }} />
						</motion.div>
					)}

					{/* Results */}
					{searchState === 'results' && (
						<motion.div
							key="results"
							initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<Box sx={{ width: '100%', boxSizing: 'border-box' }}>
								<SearchResultsHeadline count={results.length} query={query} />

								<Box
									sx={{
										position: 'sticky',
										top: 0,
										zIndex: 2,
										pt: 8,
										backgroundColor: overlayBrown,
										pb: { xs: 10, md: 12 },
										width: '100%',
										boxSizing: 'border-box'
									}}
								>
									<SearchFilterRow idPrefix="mega-menu-search-filter" {...filterRowProps} />
								</Box>

								<Box
									component="ul"
									sx={{
										listStyle: 'none',
										m: 0,
										p: 0,
										display: 'flex',
										flexDirection: 'column',
										gap: { xs: 2, md: 3 },
										width: '100%',
										pb: { xs: 10, md: 4 }
									}}
								>
									{results.map((result) => (
										<SearchResultCard key={result.id} result={result} />
									))}
								</Box>
							</Box>
						</motion.div>
					)}

					{/* Empty */}
					{searchState === 'empty' && (
						<motion.div
							key="empty"
							initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<Box sx={{ width: '100%', boxSizing: 'border-box' }}>
								<SearchResultsHeadline count={0} query={query} />

								<Box
									sx={{
										mt: 8,
										pb: { xs: 6, md: 12 },
										width: '100%',
										boxSizing: 'border-box'
									}}
								>
									<SearchFilterRow idPrefix="mega-menu-search-filter-empty" {...filterRowProps} />
								</Box>

								<Box sx={{ pl: { xs: 0, md: 4 } }}>
									<Typography component="p" sx={emptyStateLeadSx}>
										{emptyStateHeading}
									</Typography>
									<Typography component="p" sx={emptyStateLeadSx}>
										{emptyStateContactLabel}
									</Typography>

									<Box sx={{ mt: 6 }}>
										<Typography component="p" sx={emptyStateDetailSx}>
											{emptyStateEmailLine}
										</Typography>
										<Typography component="p" sx={emptyStateDetailSx}>
											{emptyStatePhoneLine}
										</Typography>
									</Box>
								</Box>
							</Box>
						</motion.div>
					)}
				</AnimatePresence>
			</Box>
		</Box>
	);
};

export default SearchView;
